import React, { useEffect, useMemo, useRef, useState } from "react";
import logo from "../../assets/images/logo-vektorski-1.png";
import DynamicModal from "../Modal/DynamicModal";
import { toast } from "react-toastify";
import "./ReportModeler.css";
import { BASE_URL } from "../../config";
import { getAccessToken } from "../../utils/LoginForm/Auth/auth";
import { MdEditNote, MdDelete } from "react-icons/md";
import {
    CgMenuLeftAlt,
    CgMenuRightAlt,
    CgFormatCenter,
    CgFormatJustify,
} from "react-icons/cg";

const ReportModeler = ({ isModalOpen, setIsModalOpen, setOverlay }) => {
    const [sections, setSections] = useState([]);
    const [newText, setNewText] = useState("");
    const [fontSize, setFontSize] = useState(14);
    const [fontWeight, setFontWeight] = useState("normal");
    const [fontStyle, setFontStyle] = useState("normal");
    const [textDecoration, setTextDecoration] = useState("none");
    const [fontFamily, setFontFamily] = useState("Arial");
    const [tableList, setTableList] = useState([]);
    const [selectedTable, setSelectedTable] = useState("");
    const [columns, setColumns] = useState([]);
    const [selectedColumn, setSelectedColumn] = useState("");
    const [selectedOperator, setSelectedOperator] = useState("=");
    const [selectedValue, setSelectedValue] = useState("");
    const [chartTable, setChartTable] = useState("");
    const [chartColumns, setChartColumns] = useState([]);
    const [chartX, setChartX] = useState("");
    const [chartY, setChartY] = useState("");
    const [chartWarning, setChartWarning] = useState("");
    const [editingIndex, setEditingIndex] = useState(null);
    const [textAlign, setTextAlign] = useState("left");
    const [filters, setFilters] = useState([]);
    const [editingFilterIndex, setEditingFilterIndex] = useState(null);

    const mountedRef = useRef(false);
    const controllersRef = useRef([]);

    const token = getAccessToken();

    const headers = useMemo(() => {
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [token]);

    const createController = () => {
        const controller = new AbortController();
        controllersRef.current.push(controller);
        return controller;
    };

    const abortAllRequests = () => {
        controllersRef.current.forEach((controller) => controller.abort());
        controllersRef.current = [];
    };

    const parseColumnsFromDescribeFeatureType = (str) => {
        const parser = new DOMParser();
        const xml = parser.parseFromString(str, "text/xml");

        return Array.from(xml.querySelectorAll("xsd\\:element, element"))
            .filter((el) => el.getAttribute("name") && el.getAttribute("type"))
            .map((el) => ({
                name: el.getAttribute("name"),
                type: el.getAttribute("type"),
            }))
            .filter((col) => col.name !== "the_geom");
    };

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            abortAllRequests();
        };
    }, []);

    useEffect(() => {
        if (!isModalOpen) {
            abortAllRequests();
            return;
        }

        const controller = createController();

        const loadTables = async () => {
            try {
                const res = await fetch(
                    `${BASE_URL}/geoserver/ows?service=WFS&version=1.0.0&request=GetCapabilities`,
                    {
                        headers,
                        signal: controller.signal,
                    }
                );

                const str = await res.text();

                if (!mountedRef.current || controller.signal.aborted) return;

                const parser = new DOMParser();
                const xml = parser.parseFromString(str, "text/xml");

                const layers = Array.from(
                    xml.querySelectorAll("FeatureType > Name")
                ).map((el) => el.textContent);

                setTableList(layers);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Greška pri učitavanju tabela:", err);
                }
            }
        };

        loadTables();

        return () => {
            controller.abort();
        };
    }, [isModalOpen, headers]);

    const resetTextFields = () => {
        setNewText("");
        setFontSize(14);
        setFontWeight("normal");
        setFontStyle("normal");
        setTextDecoration("none");
        setFontFamily("Arial");
        setTextAlign("left");
    };

    const handleAddSection = () => {
        if (!newText.trim()) return;

        const newSection = {
            type: "text",
            text: newText,
            style: {
                fontSize,
                fontWeight,
                fontStyle,
                textDecoration,
                fontFamily,
                textAlign,
            },
        };

        if (editingIndex !== null) {
            const updated = [...sections];
            updated[editingIndex] = newSection;
            setSections(updated);
            setEditingIndex(null);
        } else {
            setSections((prev) => [...prev, newSection]);
        }

        resetTextFields();
    };

    const handleRemoveSection = (indexToRemove) => {
        setSections((prev) => prev.filter((_, index) => index !== indexToRemove));

        if (editingIndex === indexToRemove) {
            setEditingIndex(null);
            resetTextFields();
        }
    };

    const handleExport = async () => {
        const payload = sections
            .map((sec) => {
                if (sec.type === "text") {
                    return {
                        type: "text",
                        content: sec.text,
                        style: sec.style,
                    };
                }

                if (sec.type === "table") {
                    return {
                        type: "table",
                        ...sec.meta,
                    };
                }

                if (sec.type === "chart") {
                    return {
                        type: "chart",
                        ...sec.meta,
                    };
                }

                return null;
            })
            .filter(Boolean);

        setOverlay({ active: true, message: "Generišem izvještaj, molimo sačekajte..." });

        try {
            const res = await fetch(`${BASE_URL}/api/generate-report`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...headers,
                },
                body: JSON.stringify({ sections: payload }),
            });

            if (!mountedRef.current) return;

            if (!res.ok) {
                const error = await res.json();
                console.error("Greška:", error);
                toast.error("Greška prilikom generisanja izvještaja.");
                return;
            }

            const blob = await res.blob();

            if (!mountedRef.current) return;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");

            a.href = url;
            a.download = "izvjestaj.zip";

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
            setSections([]);
        } catch (err) {
            if (mountedRef.current) {
                console.error("Greška u mreži:", err);
                toast.error("Desila se mrežna greška.");
            }
        } finally {
            if (mountedRef.current) {
                setOverlay({ active: false, message: "" });
            }
        }
    };

    const handleTableChange = async (e) => {
        const table = e.target.value;

        setSelectedTable(table);
        setColumns([]);
        setSelectedColumn("");
        setSelectedOperator("=");
        setSelectedValue("");
        setFilters([]);

        if (!table) return;

        const controller = createController();

        try {
            const res = await fetch(
                `${BASE_URL}/geoserver/ows?service=WFS&version=1.0.0&request=DescribeFeatureType&typeName=${table}`,
                {
                    headers,
                    signal: controller.signal,
                }
            );

            const str = await res.text();

            if (!mountedRef.current || controller.signal.aborted) return;

            setColumns(parseColumnsFromDescribeFeatureType(str));
        } catch (err) {
            if (err.name !== "AbortError") {
                console.error("Greška pri učitavanju kolona:", err);
            }
        }
    };

    const handleAddTableToReport = () => {
        if (!selectedTable) return;

        const newTableSection = {
            type: "table",
            meta: {
                table: selectedTable,
                filters,
            },
        };

        if (editingIndex !== null) {
            const updatedSections = [...sections];
            updatedSections[editingIndex] = newTableSection;
            setSections(updatedSections);
            setEditingIndex(null);
        } else {
            setSections((prev) => [...prev, newTableSection]);
        }

        setSelectedTable("");
        setColumns([]);
        setFilters([]);
        setSelectedColumn("");
        setSelectedOperator("=");
        setSelectedValue("");
        setEditingFilterIndex(null);
    };

    const handleChartTableChange = async (e) => {
        const table = e.target.value;

        setChartTable(table);
        setChartX("");
        setChartY("");
        setChartWarning("");
        setChartColumns([]);

        if (!table) return;

        const controller = createController();

        try {
            const res = await fetch(
                `${BASE_URL}/geoserver/ows?service=WFS&version=1.0.0&request=DescribeFeatureType&typeName=${table}`,
                {
                    headers,
                    signal: controller.signal,
                }
            );

            const str = await res.text();

            if (!mountedRef.current || controller.signal.aborted) return;

            const cols = parseColumnsFromDescribeFeatureType(str).map((col) => col.name);
            setChartColumns(cols);

            if (table === "elektro:vod") {
                setChartX("naziv_voda");
                setChartY("broj_stubnih_mjesta_u_vodu");
            } else if (table === "elektro:trafo_stanice") {
                setChartX("naziv");
                setChartY("broj_mjernih_mjesta");
            } else {
                setChartWarning("Za ovu tabelu grafik nije podržan.");
            }
        } catch (err) {
            if (err.name !== "AbortError") {
                console.error("Greška pri učitavanju kolona za grafikon:", err);
            }
        }
    };

    const handleCloseModal = () => {
        abortAllRequests();

        setIsModalOpen(false);
        setSections([]);
        setEditingIndex(null);
        setEditingFilterIndex(null);

        resetTextFields();

        setSelectedTable("");
        setColumns([]);
        setSelectedColumn("");
        setSelectedOperator("=");
        setSelectedValue("");
        setFilters([]);

        setChartTable("");
        setChartColumns([]);
        setChartX("");
        setChartY("");
        setChartWarning("");
    };

    const handleEditSection = (indexToEdit) => {
        const sectionToEdit = sections[indexToEdit];

        if (sectionToEdit.type !== "text") return;

        setNewText(sectionToEdit.text);
        setFontSize(sectionToEdit.style.fontSize);
        setFontWeight(sectionToEdit.style.fontWeight);
        setFontStyle(sectionToEdit.style.fontStyle);
        setTextDecoration(sectionToEdit.style.textDecoration);
        setFontFamily(sectionToEdit.style.fontFamily);
        setTextAlign(sectionToEdit.style.textAlign || "left");
        setEditingIndex(indexToEdit);
    };

    const handleAddFilter = () => {
        if (!selectedColumn || !selectedOperator || !selectedValue) return;

        const filter = {
            column: selectedColumn,
            operator: selectedOperator,
            value: selectedValue,
        };

        if (editingFilterIndex !== null) {
            const updatedFilters = [...filters];
            updatedFilters[editingFilterIndex] = filter;
            setFilters(updatedFilters);
            setEditingFilterIndex(null);
        } else {
            setFilters((prev) => [...prev, filter]);
        }

        setSelectedColumn("");
        setSelectedOperator("=");
        setSelectedValue("");
    };

    const handleEditTableSection = async (indexToEdit) => {
        const sectionToEdit = sections[indexToEdit];

        if (sectionToEdit.type !== "table") return;

        setSelectedTable(sectionToEdit.meta.table);
        setFilters(sectionToEdit.meta.filters || []);
        setEditingIndex(indexToEdit);
        setColumns([]);

        const controller = createController();

        try {
            const res = await fetch(
                `${BASE_URL}/geoserver/ows?service=WFS&version=1.0.0&request=DescribeFeatureType&typeName=${sectionToEdit.meta.table}`,
                {
                    headers,
                    signal: controller.signal,
                }
            );

            const str = await res.text();

            if (!mountedRef.current || controller.signal.aborted) return;

            setColumns(parseColumnsFromDescribeFeatureType(str));
        } catch (err) {
            if (err.name !== "AbortError") {
                console.error("Greška pri učitavanju kolona tabele:", err);
            }
        }
    };

    const handleAddChartToReport = () => {
        if (!chartTable || !chartX || !chartY) return;

        setSections((prev) => [
            ...prev,
            {
                type: "chart",
                meta: {
                    table: chartTable,
                    x: chartX,
                    y: chartY,
                },
            },
        ]);

        setChartTable("");
        setChartColumns([]);
        setChartX("");
        setChartY("");
        setChartWarning("");
    };

    return (
        <DynamicModal
            isOpen={isModalOpen}
            onRequestClose={handleCloseModal}
            title="Modeler izvještaja"
            mainContent={
                <div className="report-modeler-container">
                    <div className="report-modeler-editor">
                        <div className="textArea-builder">
                            <h4>Dodaj tekstualni blok</h4>

                            {editingIndex !== null && (
                                <div className="editing-indicator">
                                    <p>
                                        <strong>Uređujete stavku #{editingIndex + 1}</strong>
                                    </p>
                                </div>
                            )}

                            <textarea
                                rows={8}
                                value={newText}
                                onChange={(e) => setNewText(e.target.value)}
                                placeholder="Unesi tekst..."
                            />

                            <div className="text-style-container">
                                <div className="font-settings-row">
                                    <select
                                        value={fontFamily}
                                        onChange={(e) => setFontFamily(e.target.value)}
                                        className="font-select"
                                    >
                                        <option value="Arial">Arial</option>
                                        <option value="Times New Roman">Times New Roman</option>
                                        <option value="Courier New">Courier New</option>
                                        <option value="Verdana">Verdana</option>
                                    </select>

                                    <input
                                        type="number"
                                        value={fontSize}
                                        min={10}
                                        max={36}
                                        onChange={(e) =>
                                            setFontSize(Number(e.target.value) || 14)
                                        }
                                        title="Veličina fonta"
                                        className="font-size-input"
                                    />
                                </div>

                                <div className="text-style-buttons">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFontWeight((prev) =>
                                                prev === "bold" ? "normal" : "bold"
                                            )
                                        }
                                        className={`style-btn ${fontWeight === "bold" ? "active" : ""
                                            }`}
                                    >
                                        B
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFontStyle((prev) =>
                                                prev === "italic" ? "normal" : "italic"
                                            )
                                        }
                                        className={`style-btn ${fontStyle === "italic" ? "active" : ""
                                            }`}
                                    >
                                        I
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setTextDecoration((prev) =>
                                                prev === "underline" ? "none" : "underline"
                                            )
                                        }
                                        className={`style-btn ${textDecoration === "underline" ? "active" : ""
                                            }`}
                                    >
                                        U
                                    </button>
                                </div>

                                <div className="text-style-buttons">
                                    <button
                                        type="button"
                                        onClick={() => setTextAlign("left")}
                                        className={`style-btn ${textAlign === "left" ? "active" : ""
                                            }`}
                                    >
                                        <CgMenuLeftAlt />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setTextAlign("center")}
                                        className={`style-btn ${textAlign === "center" ? "active" : ""
                                            }`}
                                    >
                                        <CgFormatCenter />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setTextAlign("right")}
                                        className={`style-btn ${textAlign === "right" ? "active" : ""
                                            }`}
                                    >
                                        <CgMenuRightAlt />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setTextAlign("justify")}
                                        className={`style-btn ${textAlign === "justify" ? "active" : ""
                                            }`}
                                    >
                                        <CgFormatJustify />
                                    </button>
                                </div>
                            </div>

                            <button
                                className="report-modeler-editor-btn"
                                onClick={handleAddSection}
                            >
                                {editingIndex !== null ? "Sačuvaj izmjenu" : "Dodaj u izvještaj"}
                            </button>
                        </div>

                        <div className="cql-filter-builder">
                            <h4>Izbor tabele</h4>

                            <select value={selectedTable} onChange={handleTableChange}>
                                <option value="">-- Izaberi tabelu --</option>
                                {tableList.map((table) => (
                                    <option key={table} value={table}>
                                        {table}
                                    </option>
                                ))}
                            </select>

                            {selectedTable && (
                                <>
                                    <h5>Izbor kolone</h5>

                                    <select
                                        value={selectedColumn}
                                        onChange={(e) => setSelectedColumn(e.target.value)}
                                    >
                                        <option value="">-- Izaberi kolonu --</option>
                                        {columns.map((col) => (
                                            <option key={col.name} value={col.name}>
                                                {col.name}
                                            </option>
                                        ))}
                                    </select>

                                    <h5>Operator</h5>

                                    <select
                                        value={selectedOperator}
                                        onChange={(e) => setSelectedOperator(e.target.value)}
                                    >
                                        <option value="=">=</option>
                                        <option value="<>">&ne;</option>
                                        <option value=">">&gt;</option>
                                        <option value="<">&lt;</option>
                                        <option value=">=">&ge;</option>
                                        <option value="<=">&le;</option>
                                        <option value="LIKE">LIKE</option>
                                    </select>

                                    <h5>Vrijednost</h5>

                                    <input
                                        type="text"
                                        value={selectedValue}
                                        onChange={(e) => setSelectedValue(e.target.value)}
                                    />

                                    <button onClick={handleAddFilter}>
                                        {editingFilterIndex !== null
                                            ? "Sačuvaj filter"
                                            : "Dodaj filter"}
                                    </button>

                                    <ul>
                                        {filters.map((f, idx) => (
                                            <li
                                                key={`${f.column}-${f.operator}-${f.value}-${idx}`}
                                                onClick={() => {
                                                    setSelectedColumn(f.column);
                                                    setSelectedOperator(f.operator);
                                                    setSelectedValue(f.value);
                                                    setEditingFilterIndex(idx);
                                                }}
                                            >
                                                {f.column} {f.operator} {f.value}
                                                {editingFilterIndex === idx && " (Uređuje se)"}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {selectedTable && (
                                <button
                                    className="report-modeler-editor-btn"
                                    onClick={handleAddTableToReport}
                                >
                                    {editingIndex !== null
                                        ? "Sačuvaj tabelu"
                                        : "Dodaj tabelu u izvještaj"}
                                </button>
                            )}
                        </div>

                        <div className="chart-builder">
                            <h4>Dodavanje grafikona</h4>

                            <select value={chartTable} onChange={handleChartTableChange}>
                                <option value="">-- Izaberi tabelu --</option>
                                {tableList.map((table) => (
                                    <option key={table} value={table}>
                                        {table}
                                    </option>
                                ))}
                            </select>

                            {chartWarning && <p style={{ color: "red" }}>{chartWarning}</p>}

                            {!chartWarning && chartX && chartY && (
                                <>
                                    <p>
                                        <strong>X:</strong> {chartX}
                                    </p>
                                    <p>
                                        <strong>Y:</strong> {chartY}
                                    </p>

                                    <button
                                        className="report-modeler-editor-btn"
                                        onClick={handleAddChartToReport}
                                    >
                                        Dodaj grafikon u izvještaj
                                    </button>
                                </>
                            )}
                        </div>

                        <button onClick={handleExport} className="export-btn">
                            Generiši PDF
                        </button>
                    </div>

                    <div id="report-preview" className="report-modeler-preview">
                        <div className="report-header">
                            <img
                                src={logo}
                                alt="Elektrokrajina logo"
                                className="report-logo"
                            />
                        </div>

                        {sections.map((section, index) => (
                            <div key={index} className="report-section">
                                <div className="section-content-row">
                                    <div
                                        className={`section-text ${editingIndex === index ? "editing-section" : ""
                                            }`}
                                        style={section.type === "text" ? section.style : undefined}
                                    >
                                        {section.type === "text" && section.text}

                                        {section.type === "table" && (
                                            <div className="placeholder-section">
                                                <strong>📊 Sekcija za tabelu</strong>
                                                <p>
                                                    <strong>Tabela:</strong> {section.meta.table}
                                                </p>

                                                <div>
                                                    <strong>Filteri:</strong>
                                                    <ul>
                                                        {(section.meta.filters || []).map((f, idx) => (
                                                            <li
                                                                key={`${f.column}-${f.operator}-${f.value}-${idx}`}
                                                            >
                                                                {f.column} {f.operator} {f.value}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                        {section.type === "chart" && (
                                            <div className="placeholder-section">
                                                <strong>📈 Sekcija za grafikon</strong>
                                                <p>
                                                    <strong>Tabela:</strong> {section.meta.table}
                                                </p>
                                                <p>
                                                    <strong>X:</strong> {section.meta.x}
                                                </p>
                                                <p>
                                                    <strong>Y:</strong> {section.meta.y}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handleRemoveSection(index)}
                                        className="remove-btn hide-in-pdf"
                                        title="Ukloni"
                                    >
                                        <MdDelete />
                                    </button>

                                    {section.type === "text" && (
                                        <button
                                            onClick={() => handleEditSection(index)}
                                            className="edit-btn hide-in-pdf"
                                            title="Izmijeni"
                                        >
                                            <MdEditNote />
                                        </button>
                                    )}

                                    {section.type === "table" && (
                                        <button
                                            onClick={() => handleEditTableSection(index)}
                                            className="edit-btn hide-in-pdf"
                                            title="Izmijeni tabelu"
                                        >
                                            <MdEditNote />
                                        </button>
                                    )}
                                </div>

                                <hr className="page-break" />
                            </div>
                        ))}

                    </div>
                </div>
            }
            footerContent={
                <button
                    onClick={handleCloseModal}
                    className="app-btn app-btn-secondary"
                >
                    Zatvori
                </button>
            }
        />
    );
};

export default ReportModeler;